import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faBolt,
  faChartLine,
  faCheckCircle,
  faExternalLinkAlt,
  faKey,
  faLock,
  faShieldAlt,
  faStream,
} from '@fortawesome/free-solid-svg-icons';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Navbar,
  Nav,
  Row,
} from '@themesberg/react-bootstrap';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';

import Code from '../../components/Code';
import { useAuth } from '../../context/AuthContext';
import { Routes } from '../../routes';

const decideExample = `POST /v1/decide
{
  "eventType": "auth.login_attempt",
  "entity": { "type": "user", "id": "user-123" },
  "subjects": { "email": "user@example.com", "ip": "203.0.113.42" },
  "attributes": { "channel": "web" },
  "timestamp": "2026-06-01T12:00:00Z"
}

→ { "decision": "ALLOW" | "REVIEW" | "BLOCK" }`;

function FeatureCard({ icon, title, description }) {
  return (
    <Col xs={12} sm={6} lg={3}>
      <Card className="bg-white shadow-soft text-primary rounded mb-4 h-100">
        <div className="px-3 px-lg-4 py-5 text-center">
          <span className="icon icon-lg mb-4">
            <FontAwesomeIcon icon={icon} />
          </span>
          <h5 className="fw-bold text-primary">{title}</h5>
          <p className="mb-0 text-gray">{description}</p>
        </div>
      </Card>
    </Col>
  );
}

function Step({ number, title, body }) {
  return (
    <Col xs={12} md={4} className="mb-4 mb-md-0">
      <div className="d-flex flex-column align-items-center text-center px-3">
        <div className="icon icon-shape icon-lg bg-primary text-white rounded-circle mb-3">
          <span className="fw-bold">{number}</span>
        </div>
        <h5 className="fw-bold">{title}</h5>
        <p className="text-gray mb-0">{body}</p>
      </div>
    </Col>
  );
}

function UseCase({ title, example }) {
  return (
    <Col xs={12} md={6} lg={4} className="mb-4">
      <Card border="light" className="shadow-sm h-100">
        <Card.Body>
          <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
          <span className="fw-bold">{title}</span>
          <p className="text-gray small mt-2 mb-0">{example}</p>
        </Card.Body>
      </Card>
    </Col>
  );
}

export default function Welcome() {
  const { isAuthenticated } = useAuth();

  return (
    <>
      <Navbar variant="dark" expand="lg" bg="dark" className="navbar-transparent navbar-theme-primary sticky-top">
        <Container className="position-relative justify-content-between px-3">
          <Navbar.Brand as={HashLink} to="/#home" className="me-lg-3 d-flex align-items-center">
            <FontAwesomeIcon icon={faShieldAlt} className="text-secondary me-2" size="lg" />
            <span className="brand-text fw-bold">Flowfence</span>
          </Navbar.Brand>

          <div className="d-flex align-items-center">
            <Navbar.Collapse id="navbar-welcome">
              <Nav className="navbar-nav-hover align-items-lg-center">
                <Nav.Link as={HashLink} to="/#features">Features</Nav.Link>
                <Nav.Link as={HashLink} to="/#how-it-works">How it works</Nav.Link>
                <Nav.Link as={HashLink} to="/#use-cases">Use cases</Nav.Link>
                <Nav.Link as={Link} to={Routes.Developers.path}>API</Nav.Link>
              </Nav>
            </Navbar.Collapse>
            {isAuthenticated ? (
              <Button as={Link} to={Routes.Dashboard.path} variant="secondary" className="ms-3 text-dark">
                Dashboard <FontAwesomeIcon icon={faExternalLinkAlt} className="d-none d-sm-inline ms-1" />
              </Button>
            ) : (
              <>
                <Button as={Link} to={Routes.Login.path} variant="outline-white" className="ms-3">
                  Sign in
                </Button>
                <Button as={Link} to={Routes.Register.path} variant="secondary" className="ms-2 text-dark">
                  Start free
                </Button>
              </>
            )}
          </div>
        </Container>
      </Navbar>

      <main>
      <section
        className="section-header overflow-hidden pt-5 pt-lg-6 pb-9 pb-lg-12 bg-primary text-white"
        id="home"
      >
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} lg={10} className="text-center">
              <Badge bg="secondary" className="text-dark mb-3 px-3 py-2">
                Real-time risk decisioning API
              </Badge>
              <h1 className="fw-bolder text-secondary display-4">
                Catch abuse patterns before they become losses
              </h1>
              <p className="text-muted fw-light mb-3 h5 px-lg-5">
                Flowfence helps teams detect suspicious activity across logins, signups,
                payments, withdrawals, loan approvals, OTP requests, and API traffic.
              </p>
              <p className="text-muted fw-light mb-5 h5 px-lg-5">
                Send one structured event and receive an explainable ALLOW, REVIEW, or BLOCK
                decision in milliseconds — powered by blocklists, velocity rules, and
                customer-specific risk controls.
              </p>
              <div className="d-flex flex-wrap align-items-center justify-content-center gap-2">
                {isAuthenticated ? (
                  <>
                    <Button variant="secondary" as={Link} to={Routes.Dashboard.path} className="text-dark">
                      Open console <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-1" />
                    </Button>
                    <Button variant="outline-white" as={Link} to={Routes.Developers.path}>
                      Integrate the API
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" as={Link} to={Routes.Register.path} className="text-dark">
                      Start free
                    </Button>
                    <Button variant="outline-white" as={Link} to={Routes.Login.path}>
                      Sign in
                    </Button>
                    <Button variant="outline-white" as={Link} to={Routes.Developers.path}>
                      Integrate the API
                    </Button>
                  </>
                )}
              </div>
            </Col>
          </Row>
          <figure className="position-absolute bottom-0 left-0 w-100 d-none d-md-block mb-n2">
            <svg className="fill-soft" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3000 185.4">
              <path d="M3000,0v185.4H0V0c496.4,115.6,996.4,173.4,1500,173.4S2503.6,115.6,3000,0z" />
            </svg>
          </figure>
        </Container>
      </section>

      <section className="section pt-0">
        <Container className="mt-n8 mt-lg-n10 z-2">
          <Row className="justify-content-center">
            <Col xs={12} lg={10}>
              <Card border="light" className="shadow-lg">
                <Card.Body className="p-2 p-md-4">
                  <Code code={decideExample} language="json" />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="section section-md bg-soft pt-lg-5" id="features">
        <Container>
          <Row className="justify-content-center mb-5 mb-lg-6">
            <Col xs={12} lg={8} className="text-center">
              <h2 className="fw-bolder">Built for real-time risk</h2>
              <p className="lead text-gray">
                Everything you need to evaluate events at scale — without building your own
                velocity engine, blocklist, or token vault from scratch.
              </p>
            </Col>
          </Row>
          <Row>
            <FeatureCard
              icon={faBan}
              title="Identifier blocklist"
              description="Block known risky identifiers before rules run — emails, phones, IPs, devices, accounts, or custom IDs."
            />
            <FeatureCard
              icon={faChartLine}
              title="Velocity rules"
              description="Define COUNT, SUM, and DISTINCT rules over sliding windows — failed logins per email, OTPs per phone, withdrawals per account, or loan approvals per approver."
            />
            <FeatureCard
              icon={faBolt}
              title="Single decide endpoint"
              description="Send one structured event and receive one explainable decision: ALLOW, REVIEW, or BLOCK."
            />
            <FeatureCard
              icon={faLock}
              title="PII tokenization"
              description="Tokenize sensitive identifiers so rules can run without storing raw emails, phone numbers, account numbers, or government IDs."
            />
          </Row>
        </Container>
      </section>

      <section className="section section-md" id="how-it-works">
        <Container>
          <Row className="justify-content-center mb-5">
            <Col xs={12} lg={8} className="text-center">
              <h2 className="fw-bolder">How a decision is made</h2>
              <p className="lead text-gray">
                Every event flows through a predictable pipeline so you can explain every outcome.
              </p>
            </Col>
          </Row>
          <Row>
            <Step
              number="1"
              title="Send an event"
              body="POST login attempts, withdrawals, loan approvals, OTP requests, profile changes, or API calls."
            />
            <Step
              number="2"
              title="Evaluate rules"
              body="Flowfence checks blocklists, velocity limits, and customer-specific risk rules in real time."
            />
            <Step
              number="3"
              title="Return a decision"
              body="Get ALLOW, REVIEW, or BLOCK with the rules that matched and the action to take."
            />
          </Row>
          <Row className="justify-content-center mt-5">
            <Col xs={12} lg={10}>
              <Card bg="dark" text="white" className="border-0 shadow">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <FontAwesomeIcon icon={faStream} className="text-secondary me-2" />
                    <span className="fw-bold">Decision pipeline</span>
                  </div>
                  <div className="d-flex flex-wrap align-items-center justify-content-center gap-2 text-center">
                    <Badge bg="secondary" className="text-dark px-3 py-2">Event in</Badge>
                    <span className="text-muted">→</span>
                    <Badge bg="danger" className="px-3 py-2">Blocklist check</Badge>
                    <span className="text-muted">→</span>
                    <Badge bg="warning" className="text-dark px-3 py-2">Velocity rules</Badge>
                    <span className="text-muted">→</span>
                    <Badge bg="success" className="px-3 py-2">ALLOW / REVIEW / BLOCK</Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="section section-md bg-soft" id="use-cases">
        <Container>
          <Row className="justify-content-center mb-5">
            <Col xs={12} lg={8} className="text-center">
              <h2 className="fw-bolder">Where teams use Flowfence</h2>
              <p className="lead text-gray">
                Any high-volume stream where you need consistent, explainable risk decisions.
              </p>
            </Col>
          </Row>
          <Row>
            <UseCase
              title="Login abuse"
              example="Detect repeated failed logins, suspicious IPs, and new-device activity before accounts are compromised."
            />
            <UseCase
              title="Signup fraud"
              example="Rate-limit OTP requests, disposable emails, duplicate devices, and repeated account creation attempts."
            />
            <UseCase
              title="Payments & withdrawals"
              example="Apply velocity limits to transaction counts, amounts, beneficiaries, cards, accounts, or devices."
            />
            <UseCase
              title="Loan approval abuse"
              example="Flag unusual approval patterns, approval spikes, off-hours activity, or risky borrower profile changes."
            />
            <UseCase
              title="API abuse"
              example="Count requests per API key, IP, tenant, or endpoint in rolling windows before your backend is overwhelmed."
            />
            <UseCase
              title="Sensitive data protection"
              example="Tokenize account numbers, phone numbers, government IDs, and other sensitive identifiers before they reach your application database."
            />
          </Row>
        </Container>
      </section>

      <section className="section section-md bg-primary text-white">
        <Container>
          <Row className="justify-content-center text-center">
            <Col xs={12} lg={8}>
              <FontAwesomeIcon icon={faKey} className="text-secondary mb-3" size="2x" />
              <h2 className="fw-bolder text-secondary mb-3">Ready to try it?</h2>
              <p className="text-muted fw-light mb-4 h5">
                Create a workspace, define your first rule, and send a test event from the console —
                no risk infrastructure to build from scratch.
              </p>
              {isAuthenticated ? (
                <Button variant="secondary" as={Link} to={Routes.Decide.path} className="text-dark">
                  Try the decision console
                </Button>
              ) : (
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  <Button variant="secondary" as={Link} to={Routes.Register.path} className="text-dark">
                    Start free
                  </Button>
                  <Button variant="outline-white" as={Link} to={Routes.Login.path}>
                    Sign in
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>
      </main>

      <footer className="footer section pt-4 pb-3 bg-dark text-gray">
        <Container>
          <Row className="align-items-center">
            <Col xs={12} md={6} className="text-center text-md-start mb-3 mb-md-0">
              <FontAwesomeIcon icon={faShieldAlt} className="text-secondary me-2" />
              <span className="fw-bold text-white">Flowfence</span>
              <span className="ms-2">— real-time risk decisions for product events</span>
            </Col>
            <Col xs={12} md={6} className="text-center text-md-end">
              <Link to={Routes.Login.path} className="text-gray me-3">Sign in</Link>
              <Link to={Routes.Register.path} className="text-gray">Register</Link>
            </Col>
          </Row>
        </Container>
      </footer>
    </>
  );
}
